<template>
  <div
    class="select-input"
    :class="{
      'select-input--open': showValues && values.length > 0,
      'select-input--searchable': searchable,
      'select-input--error': errorMessage,
      'select-input--loading': loading
    }"
  >
    <div
      class="text-input"
      :class="{
        'text-input--has-content': !!searchTerm
      }"
    >
      <input
        v-model="val"
        class="select-input__hidden"
        type="hidden"
        v-bind="attributes"
      >

      <div
        v-if="!!$slots.selected"
        :id="name"
        ref="input"
        :aria-activedescendant="showValues && values.length > 0 ? `${name}-value-${focusedValueIndex}` : null"
        class="text-input__input text-input__input--custom"
        tabindex="0"
        role="button"
        data-fake-input
        v-on="listeners"
      >
        <slot
          v-if="selectedOption"
          name="selected"
          :option="selectedOption"
        />
        <slot
          v-else
          name="placeholder"
        />
      </div>

      <input
        v-else
        :id="name"
        ref="input"
        v-model="searchTerm"
        :placeholder="placeholder"
        :readonly="!searchable"
        :aria-activedescendant="showValues && values.length > 0 ? `${name}-value-${focusedValueIndex}` : null"
        class="text-input__input"
        type="search"
        v-on="listeners"
      >

      <label
        v-if="!$slots.selected"
        class="text-input__label"
        :for="name"
      >
        {{ label }}
      </label>

      <template v-if="searchable">
        <button
          v-if="searchTerm"
          type="button"
          class="select-input__button"
          aria-label="clear"
          @click.prevent="clearSearchTerm"
        >
          <span class="icon fa fa-times-circle" />
        </button>
        <div
          v-else
          class="select-input__icon"
        >
          <span class="icon far fa-search" />
        </div>
      </template>
      <div
        v-else
        class="select-input__button"
      >
        <span class="icon far fa-chevron-down" />
      </div>
    </div>

    <ul
      v-show="showValues && values.length > 0"
      ref="selectMenu"
      class="select-input__values"
    >
      <li
        v-for="(v, index) in values"
        :id="`${name}-value-${index}`"
        :key="`${name}-value-${index}`"
        ref="selectValue"
        class="select-input__value"
        :class="{
          'select-input__value--focused': index === focusedValueIndex,
          'select-input__value--custom': !!$slots.option
        }"
        @mousedown="selectValue(v)"
      >
        <slot
          name="option"
          :option="v"
        >
          {{ v.display }}
          <span
            v-if="v.price"
            class="select-input__sub-value"
          >+{{ $dollars(v.price) }}</span>
        </slot>
      </li>
    </ul>

    <input-error :error="errorMessage" />
  </div>
</template>



<script>
  import { computed, watch } from 'vue';
  import { useField } from 'vee-validate';

  import lowercase from '@/filters/lowercase';



  export default {
    name: 'SelectInput',

    props: {
      label: {
        type: String,
        required: true
      },

      loading: {
        type: Boolean,
        default: false
      },

      name: {
        type: String,
        required: true
      },

      placeholder: {
        type: String,
        default: null
      },

      searchable: {
        type: Boolean,
        default: true
      },

      validations: {
        type: Object,
        default() {
          return {};
        }
      },

      modelValue: {
        type: [String, Number],
        default: null
      },

      values: {
        type: Array,
        default() {
          return [];
        }
      }
    },

    // VeeValidate 4 requires the Composition API to register a field with the parent form context.
    // setup() is used exclusively for that purpose — all other component logic
    // remains in the Options API below.
    // Exposes: errorMessage (reactive validation error string, undefined when valid)
    // Note: handleChange is used internally by the watch() below and is not exposed.
    setup(props) {
      const rules = computed(() => props.validations);

      const { errorMessage, handleChange } = useField(() => props.name, rules, {
        initialValue: props.modelValue,
        label: props.label
      });

      watch(() => props.modelValue, handleChange);

      return { errorMessage };
    },

    data() {
      return {
        focusedValueIndex: 0,
        invisibleSearchTerm: '',
        searchTerm: null,
        showValues: false
      };
    },

    computed: {
      attributes() {
        return {
          'data-vv-as': lowercase(this.label, true),
          name: this.name
        };
      },

      listeners: {
        get() {
          return {
            ...this.$attrs,
            blur: this.blurHandler,
            focus: this.focusHandler,
            input: this.inputHandler,
            keydown: this.keyDownHandler,
            keypress: this.keyPressHandler,
            mousedown: this.mouseDownHandler
          };
        }
      },

      selectedOption() {
        return this.values.find(v => v.value === this.modelValue);
      },

      val() {
        return this.modelValue;
      }
    },

    watch: {
      focusedValueIndex: 'focusedValueIndexChangeHandler',
      modelValue: {
        handler: 'valueUpdatedHandler',
        immediate: true
      },
      values: {
        handler: 'valuesUpdatedHandler',
        immediate: true
      }
    },

    created() {
      this.createdHandler();
    },

    methods: {
      arrowDownKeyHandler() {
        if (!this.showValues) {
          this.updateShowValues(true);
        }

        else if (this.values.length - 1 > this.focusedValueIndex) {
          this.focusedValueIndex++;
        }
      },

      arrowUpKeyHandler() {
        if (this.focusedValueIndex > 0) {
          this.focusedValueIndex--;
        }
      },

      blurHandler() {
        this.updateShowValues(false);
        this.$emit('search-term-blur');
      },

      clearSearchTerm() {
        this.searchTerm = '';
        this.inputHandler();
      },

      createdHandler() {
      },

      enterKeyHandler(event) {
        const value = this.values[this.focusedValueIndex];

        this.selectValue(value);

        event.target.blur();
      },

      escapeKeyHandler(event) {
        event.target.blur();
      },

      focusHandler() {
        this.invisibleSearchTerm = '';

        this.updateShowValues(true, 'focus');

        this.$emit('search-term-focus');
      },

      async focusedValueIndexChangeHandler(index, oldIndex, eventType) {
        await this.$nextTick();

        if (!this.$refs.selectValue) {
          return;
        }

        const focusedValue = this.$refs.selectValue[index];

        if (focusedValue) {
          const delay = eventType === 'focus' ? 50 : 0;

          setTimeout(() => {
            focusedValue.scrollIntoView({
              behavior: 'auto',
              block: 'nearest',
              inline: 'start'
            });
          }, delay);
        }
      },

      focusValueMatchingInvisibleSearchTerm() {
        const search = this.invisibleSearchTerm.toLowerCase().replace(/[^a-z0-9]/g, '');

        const matchingIndex = this.values.findIndex((v) => {
          const value = v.display.toLowerCase().replace(/[^a-z0-9]/g, '');
          return value.indexOf(search) === 0;
        });

        if (matchingIndex > -1) {
          this.focusedValueIndex = matchingIndex;
        }
      },

      forceUpdateSearchTerm(searchTerm) {
        this.searchTerm = searchTerm;
      },

      inputHandler() {
        this.$emit('search-term-input', this.searchTerm);

        this.focusedValueIndex = 0;
      },

      keyDownHandler(event) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.arrowDownKeyHandler(event);
        }
        else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.arrowUpKeyHandler(event);
        }
        else if (event.key === 'Enter') {
          event.preventDefault();
          this.enterKeyHandler(event);
        }
        else if (event.key === 'Escape') {
          event.preventDefault();
          this.escapeKeyHandler(event);
        }
      },

      keyPressHandler(event) {
        if (this.searchable) {
          return;
        }

        event.preventDefault();

        this.invisibleSearchTerm += event.key;

        this.focusValueMatchingInvisibleSearchTerm();
      },

      mouseDownHandler(event) {
        if (this.showValues && !this.searchable) {
          event.preventDefault();
          event.target.blur();
        }
      },

      async selectValue(value) {
        this.updateShowValues(false);

        if (value) {
          this.searchTerm = value.display;
          this.$emit('update:modelValue', value.value);

          /*
            NOTE: The following event dispatch allows a wrapping <validate-form>
            parent component to know when a selection was changed to mark the
            form as dirty:
          */

          await this.$nextTick();

          const event = new Event('input', {
            bubbles: true
          });
          this.$refs.input.dispatchEvent(event);
        }

        else {
          this.$emit('unmatched-input', this.searchTerm);
        }
      },

      updateShowValues(showValues, eventType) {
        this.showValues = showValues;

        if (showValues) {
          const matchingValueIndex = this.values.findIndex(value => this.searchTerm === value.display);

          if (matchingValueIndex > -1) {
            this.focusedValueIndex = matchingValueIndex;
          }

          else {
            this.focusedValueIndex = 0;
          }

          this.focusedValueIndexChangeHandler(this.focusedValueIndex, null, eventType);
        }
      },

      valueUpdatedHandler(newValue) {
        const matchingValueIndex = this.values.findIndex(value => value.value === newValue);

        const matchingValue = this.values[matchingValueIndex];

        if (matchingValue) {
          this.focusedValueIndex = matchingValueIndex;
          this.searchTerm = matchingValue.display;
        }

        else {
          this.focusedValueIndex = 0;
          this.searchTerm = null;
        }
      },

      valuesUpdatedHandler(newValues) {
        const matchingValueIndex = newValues.findIndex(value => value.value === this.modelValue);

        const matchingValue = this.values[matchingValueIndex];

        if (matchingValue) {
          this.focusedValueIndex = matchingValueIndex;
          this.searchTerm = matchingValue.display;
        }
      }
    }
  };
</script>
